import React, { useState, useEffect } from 'react';
import {
  getProject,
  getProjectSummary,
  updateProject,
  uploadProjectLogo,
  getPMList,
  getProgressAlertsSummary,
  getDependencyAlerts,
} from '../api';
import SOVTab from '../components/SOVTab';
import TasksTab from '../components/TasksTab';
import GanttTab from '../components/GanttTab';
import BillingTab from '../components/BillingTab';
import ForecastTab from '../components/ForecastTab';
import ChangeOrdersTab from '../components/ChangeOrdersTab';
import ContractValueTab from '../components/ContractValueTab';
import ClientViewTab from '../components/ClientViewTab';
import RollupTab from '../components/RollupTab';
import SchedulePerformanceTab, { BillingPerformanceSection } from '../components/SchedulePerformanceTab';
import JobCostTab from '../components/JobCostTab';
import TaskEditModal from '../components/TaskEditModal';
import { useTheme } from '../hooks/useTheme';

const TABS = [
  { key: 'dashboard', label: 'Overview' },
  { key: 'sov', label: 'SOV' },
  { key: 'tasks', label: 'Tasks' },
  { key: 'rollup', label: 'Rollup' },
  { key: 'gantt', label: 'Gantt' },
  { key: 'billing', label: 'Billing' },
  { key: 'performance', label: 'Performance' },
  { key: 'forecast', label: 'Forecast' },
  { key: 'contract value', label: 'Contract Value' },
  { key: 'job-costs', label: 'Job Costs' },
  { key: 'client view', label: 'Client View' },
];

const ProjectDetail = ({ projectId, onBack, activeTab: externalTab, setActiveTab: externalSetActiveTab }) => {
  const [internalTab, setInternalTab] = useState('dashboard');
  const activeTab = externalTab || internalTab;
  const setActiveTab = externalSetActiveTab || setInternalTab;
  const [perfView, setPerfView] = useState('schedule');
  const [project, setProject] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedProject, setEditedProject] = useState(null);
  const [ganttEditTaskId, setGanttEditTaskId] = useState(null);
  const [ganttRefreshKey, setGanttRefreshKey] = useState(0);
  const [pmList, setPMList] = useState([]);
  const [logoUploading, setLogoUploading] = useState(false);
  const [editingCollected, setEditingCollected] = useState(false);
  const [collectedInput, setCollectedInput] = useState('');
  const [alertsSummary, setAlertsSummary] = useState(null);
  const [depAlerts, setDepAlerts] = useState(null);
  const t = useTheme();

  useEffect(() => {
    loadProjectData();
    getPMList().then(setPMList).catch(console.error);
    getProgressAlertsSummary(projectId).then(setAlertsSummary).catch(console.error);
    getDependencyAlerts(projectId).then(setDepAlerts).catch(console.error);
  }, [projectId]);

  const loadProjectData = async () => {
    setLoading(true);
    try {
      const [projectData, summaryData] = await Promise.all([
        getProject(projectId),
        getProjectSummary(projectId),
      ]);
      setProject(projectData);
      setSummary(summaryData);
      setEditedProject(projectData);
    } catch (error) {
      console.error('Error loading project data:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshSummary = async () => {
    try {
      const summaryData = await getProjectSummary(projectId);
      setSummary(summaryData);
    } catch (error) {
      console.error('Error refreshing summary:', error);
    }
  };

  const handleSaveProject = async () => {
    setSaving(true);
    try {
      await updateProject(projectId, editedProject);
      setProject(editedProject);
      await refreshSummary();
    } catch (error) {
      console.error('Error saving project:', error);
      alert('Failed to save project. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleEditChange = (field, value) => {
    setEditedProject({ ...editedProject, [field]: value });
  };

  const handleCollectedSave = async () => {
    const val = parseFloat(collectedInput);
    if (isNaN(val) || val < 0) return;
    try {
      await updateProject(projectId, { collected_to_date: val });
      await loadProjectData();
      setEditingCollected(false);
    } catch (err) { console.error('Failed to update collected:', err); }
  };

  const handleCurrencyEditChange = (field, value) => {
    const raw = value.replace(/[^0-9.]/g, '');
    const parts = raw.split('.');
    const cleaned = parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('') : '');
    setEditedProject({ ...editedProject, [field]: cleaned });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert('Image must be under 2MB.');
      return;
    }
    setLogoUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        // Resize image to max 200x200 for storage efficiency
        const img = new Image();
        img.onload = async () => {
          const canvas = document.createElement('canvas');
          const max = 200;
          let w = img.width, h = img.height;
          if (w > max || h > max) {
            if (w > h) { h = Math.round(h * max / w); w = max; }
            else { w = Math.round(w * max / h); h = max; }
          }
          canvas.width = w;
          canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          const dataUrl = canvas.toDataURL('image/png');
          await uploadProjectLogo(projectId, dataUrl);
          setProject(prev => ({ ...prev, logo_url: dataUrl }));
          setEditedProject(prev => ({ ...prev, logo_url: dataUrl }));
          setLogoUploading(false);
        };
        img.src = reader.result;
      } catch (err) {
        console.error('Logo upload failed:', err);
        alert('Failed to upload logo.');
        setLogoUploading(false);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleRemoveLogo = async () => {
    try {
      setLogoUploading(true);
      await uploadProjectLogo(projectId, null);
      setProject(prev => ({ ...prev, logo_url: null }));
      setEditedProject(prev => ({ ...prev, logo_url: null }));
    } catch (err) {
      console.error('Failed to remove logo:', err);
    } finally {
      setLogoUploading(false);
    }
  };

  const formatCurrencyInput = (value) => {
    if (!value || value === '') return '';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    const parts = String(value).split('.');
    const intPart = parseInt(parts[0] || '0', 10).toLocaleString('en-US');
    if (parts.length > 1) {
      return intPart + '.' + parts[1];
    }
    return intPart;
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    return `$${Number(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: t.textSecondary }}>
        Loading project data...
      </div>
    );
  }

  if (!project || !summary) {
    return (
      <div style={{ padding: '20px' }}>
        <button className="btn btn-secondary" onClick={onBack}>
          &larr; Back to Projects
        </button>
        <p style={{ color: '#f44336', marginTop: '16px' }}>Error loading project data</p>
      </div>
    );
  }

  const contractVal = summary.contract_value || 0;
  const allocatedVal = summary.allocated_value || 0;
  const billedVal = summary.billed_to_date || 0;
  const retainageVal = summary.retainage_held || 0;
  const remainingVal = summary.remaining || 0;
  const variance = contractVal - allocatedVal;
  const billedPercent = contractVal > 0 ? ((billedVal / contractVal) * 100).toFixed(1) : 0;

  // Days remaining in project
  const projectEndDate = summary.latest_task_finish || project.end_date;
  const daysRemaining = projectEndDate
    ? Math.max(0, Math.ceil((new Date(projectEndDate) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <div style={{ maxWidth: '100%', overflowX: 'hidden' }}>
      {/* Project Header */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {project.logo_url ? (
              <img src={project.logo_url} alt="" style={{
                width: 48, height: 48, borderRadius: 10, objectFit: 'cover',
                border: `2px solid ${t.cardBorder}`, flexShrink: 0,
              }} />
            ) : (
              <div style={{
                width: 48, height: 48, borderRadius: 10, background: t.bgAlt,
                border: `2px solid ${t.cardBorder}`, display: 'flex', alignItems: 'center',
                justifyContent: 'center', flexShrink: 0, fontSize: 20, fontWeight: 700,
                color: t.textMuted,
              }}>
                {(project.project_name || '?')[0].toUpperCase()}
              </div>
            )}
            <div>
              <h1 className="project-detail-title" style={{ margin: '0 0 4px 0', fontSize: '24px', fontWeight: 700 }}>
                {project.project_name}
              </h1>
            <div style={{ display: 'flex', gap: '16px', fontSize: '14px', color: t.textSecondary, flexWrap: 'wrap', alignItems: 'center' }}>
              {project.project_number && <span>#{project.project_number}</span>}
              {project.client_name && <span>{project.client_name}</span>}
              {project.project_manager && <span>PM{project.project_manager.includes(',') ? 's' : ''}: {project.project_manager}</span>}
              {summary.earliest_task_start && summary.latest_task_finish && (
                <span>{formatDate(summary.earliest_task_start)} – {formatDate(summary.latest_task_finish)}</span>
              )}
              <span
                className={`badge ${
                  project.status === 'Active'
                    ? 'badge-labor'
                    : project.status === 'Complete'
                    ? 'badge-material'
                    : 'badge-equipment'
                }`}
              >
                {project.status || 'Active'}
              </span>
            </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Contract Value</div>
          <div className="stat-value">{formatCurrency(contractVal)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">SOV Allocated</div>
          <div className="stat-value">{formatCurrency(allocatedVal)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Billed to Date</div>
          <div className="stat-value">{formatCurrency(billedVal)}</div>
          {retainageVal > 0 && (
            <div style={{ fontSize: 11, color: '#d97706', marginTop: 2 }}>+{formatCurrency(retainageVal)} retainage held</div>
          )}
        </div>
        <div className="stat-card">
          <div className="stat-label">Remaining</div>
          <div className="stat-value">{formatCurrency(remainingVal)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Variance</div>
          <div
            className="stat-value"
            style={{
              color: variance === 0 ? '#4CAF50' : variance > 0 ? '#ff9800' : '#f44336',
            }}
          >
            {formatCurrency(variance)}
          </div>
        </div>
        {daysRemaining !== null && (
          <div className="stat-card">
            <div className="stat-label">Days Remaining</div>
            <div
              className="stat-value"
              style={{
                color: daysRemaining <= 14 ? '#f44336' : daysRemaining <= 30 ? '#ff9800' : t.text,
              }}
            >
              {daysRemaining}
            </div>
          </div>
        )}
        <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => {
          if (!editingCollected) {
            setCollectedInput((parseFloat(project?.collected_to_date) || 0).toFixed(2));
            setEditingCollected(true);
          }
        }}>
          <div className="stat-label">Collected to Date</div>
          {editingCollected ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} onClick={e => e.stopPropagation()}>
              <span style={{ fontSize: 16, fontWeight: 700 }}>$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={collectedInput}
                onChange={e => setCollectedInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleCollectedSave(); if (e.key === 'Escape') setEditingCollected(false); }}
                autoFocus
                style={{
                  fontSize: 16, fontWeight: 700, width: '110px',
                  border: `1px solid ${t.cardBorder}`, borderRadius: 6, padding: '2px 6px',
                  backgroundColor: t.inputBg || t.bg, color: t.heading, outline: 'none',
                }}
              />
              <button onClick={handleCollectedSave} style={{
                padding: '2px 8px', fontSize: 11, fontWeight: 600, borderRadius: 4,
                border: 'none', background: '#4CAF50', color: '#fff', cursor: 'pointer',
              }}>Save</button>
            </div>
          ) : (
            <div className="stat-value" style={{ color: '#4CAF50' }}>
              {formatCurrency(parseFloat(project?.collected_to_date) || 0)}
            </div>
          )}
          <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>{editingCollected ? 'Enter amount received' : 'Click to update'}</div>
        </div>
        {(() => {
          const outstanding = billedVal - (parseFloat(project?.collected_to_date) || 0);
          return (
            <div className="stat-card">
              <div className="stat-label">Outstanding</div>
              <div className="stat-value" style={{ color: outstanding > 0.01 ? '#ff9800' : '#4CAF50' }}>
                {formatCurrency(Math.max(0, outstanding))}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Progress Bar */}
      <div style={{ margin: '20px 0 24px 0' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '13px',
            color: t.textSecondary,
            marginBottom: '6px',
          }}
        >
          <span style={{ fontWeight: 600 }}>Overall Billed Progress</span>
          <span>
            {billedPercent}%
            {retainageVal > 0 && (
              <span style={{ fontSize: 11, color: '#d97706', marginLeft: 8 }}>+{formatCurrency(retainageVal)} retainage held</span>
            )}
          </span>
        </div>
        <div className="progress-bar" style={{ height: '10px' }}>
          <div
            className="progress-bar-fill"
            style={{
              width: `${Math.min(billedPercent, 100)}%`,
              backgroundColor:
                billedPercent >= 100
                  ? '#2196f3'
                  : billedPercent >= 75
                  ? '#4CAF50'
                  : billedPercent >= 50
                  ? '#ff9800'
                  : '#2563eb',
            }}
          />
        </div>
      </div>


      {/* Tab Navigation */}
      <div className="tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ marginTop: '20px', maxWidth: '100%', overflowX: 'hidden' }}>
        {activeTab === 'dashboard' && (
          <div className="card" style={{ padding: '24px', backgroundColor: t.bg, border: `1px solid ${t.cardBorder}` }}>
            {/* Logo + Title */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, marginBottom: 24 }}>
              <div style={{ position: 'relative', flexShrink: 0 }}>
                {project.logo_url ? (
                  <div style={{ position: 'relative' }}>
                    <img
                      src={project.logo_url}
                      alt="Project logo"
                      style={{
                        width: 80, height: 80, borderRadius: 12, objectFit: 'cover',
                        border: `2px solid ${t.cardBorder}`, background: t.bgAlt,
                      }}
                    />
                    <button
                      onClick={handleRemoveLogo}
                      title="Remove logo"
                      style={{
                        position: 'absolute', top: -6, right: -6,
                        width: 20, height: 20, borderRadius: '50%',
                        background: '#ef4444', color: '#fff', border: 'none',
                        fontSize: 12, cursor: 'pointer', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', lineHeight: 1,
                      }}
                    >&times;</button>
                  </div>
                ) : (
                  <label
                    style={{
                      width: 80, height: 80, borderRadius: 12,
                      border: `2px dashed ${t.dark ? '#4a5568' : '#cbd5e1'}`, background: t.bgAlt,
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                      justifyContent: 'center', cursor: 'pointer', transition: 'all 0.15s ease',
                    }}
                    title="Upload project logo"
                    onMouseEnter={e => { e.currentTarget.style.borderColor = t.dark ? '#718096' : '#94a3b8'; e.currentTarget.style.background = t.dark ? '#2d3748' : '#f1f5f9'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = t.dark ? '#4a5568' : '#cbd5e1'; e.currentTarget.style.background = t.bgAlt; }}
                  >
                    <span style={{ fontSize: 24, color: '#94a3b8', lineHeight: 1 }}>+</span>
                    <span style={{ fontSize: 9, color: '#94a3b8', marginTop: 2, fontWeight: 600 }}>LOGO</span>
                    <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
                  </label>
                )}
                {logoUploading && (
                  <div style={{
                    position: 'absolute', inset: 0, borderRadius: 12,
                    background: t.dark ? 'rgba(22,33,62,0.7)' : 'rgba(255,255,255,0.7)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', fontSize: 11, color: t.textSecondary,
                  }}>...</div>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: t.heading }}>
                  Project Information
                </h2>
                {project.logo_url && (
                  <label style={{ fontSize: 12, color: t.dark ? '#60a5fa' : '#2563eb', cursor: 'pointer', marginTop: 4, display: 'inline-block' }}>
                    Change logo
                    <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
                  </label>
                )}
              </div>
            </div>
            {/* Progress Alerts */}
            {alertsSummary && alertsSummary.summaries?.length > 0 && (
              <div style={{
                padding: '14px 18px', marginBottom: 20, borderRadius: 10,
                border: '1px solid #fca5a5', backgroundColor: '#fef2f2',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 16 }}>⚠️</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#dc2626' }}>
                    Tasks Need Progress Updates
                  </span>
                </div>
                {alertsSummary.summaries.map((s, i) => (
                  <div key={i} style={{
                    padding: '8px 12px', marginBottom: i < alertsSummary.summaries.length - 1 ? 6 : 0,
                    borderRadius: 8, backgroundColor: '#fff', border: '1px solid #fecaca',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#44403c' }}>{s.period_name}</span>
                      <span style={{ fontSize: 12, color: '#78716c', marginLeft: 8 }}>
                        {s.days_until_end <= 0 ? 'Period ended' : `${s.days_until_end} day${s.days_until_end !== 1 ? 's' : ''} left`}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {s.critical_count > 0 && (
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, backgroundColor: '#dc2626', color: '#fff' }}>
                          {s.critical_count} critical
                        </span>
                      )}
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, backgroundColor: '#f59e0b', color: '#fff' }}>
                        {s.alert_count} task{s.alert_count !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                ))}
                <div style={{ fontSize: 12, color: '#78716c', marginTop: 8 }}>
                  Update task completion percentages or adjust task dates on the Tasks tab.
                </div>
              </div>
            )}

            {/* Dependency / Scheduling Alerts */}
            {depAlerts && depAlerts.summary?.total > 0 && (
              <div style={{
                padding: '14px 18px', marginBottom: 20, borderRadius: 10,
                border: depAlerts.summary.critical > 0 ? '1px solid #fca5a5' : '1px solid #fde68a',
                backgroundColor: depAlerts.summary.critical > 0 ? '#fef2f2' : '#fffbeb',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 16 }}>&#9888;&#65039;</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: depAlerts.summary.critical > 0 ? '#dc2626' : '#d97706' }}>
                    Scheduling Alerts ({depAlerts.summary.total})
                  </span>
                  <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
                    {depAlerts.summary.critical > 0 && (
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, backgroundColor: '#dc2626', color: '#fff' }}>
                        {depAlerts.summary.critical} critical
                      </span>
                    )}
                    {depAlerts.summary.warning > 0 && (
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, backgroundColor: '#d97706', color: '#fff' }}>
                        {depAlerts.summary.warning} warning
                      </span>
                    )}
                  </div>
                </div>
                {(depAlerts.alerts || []).slice(0, 5).map((alert, i) => (
                  <div key={i} style={{
                    padding: '6px 12px', marginBottom: i < Math.min(depAlerts.alerts.length, 5) - 1 ? 4 : 0,
                    borderRadius: 8, backgroundColor: '#fff',
                    border: alert.severity === 'critical' ? '1px solid #fecaca' : '1px solid #fde68a',
                    fontSize: 12, color: '#44403c',
                  }}>
                    <span style={{ color: alert.severity === 'critical' ? '#dc2626' : '#d97706', fontWeight: 600, marginRight: 6 }}>
                      {alert.type === 'circular' ? 'Circular' : alert.type === 'date_violation' ? 'Date Violation' : 'Missing Dates'}:
                    </span>
                    {alert.message}
                  </div>
                ))}
                {depAlerts.alerts.length > 5 && (
                  <div style={{ fontSize: 12, color: '#78716c', marginTop: 6 }}>
                    ...and {depAlerts.alerts.length - 5} more. Check the Gantt or Tasks tab for details.
                  </div>
                )}
                <div style={{ fontSize: 12, color: '#78716c', marginTop: 8 }}>
                  Fix dependency date conflicts or add missing dates on the Tasks or Gantt tab.
                </div>
              </div>
            )}

            <div className="form-group">
              <label style={{ color: t.heading }}>Project Name</label>
              <input
                type="text"
                value={editedProject.project_name || ''}
                onChange={(e) => handleEditChange('project_name', e.target.value)}
                style={{ backgroundColor: t.inputBg, borderColor: t.inputBorder, color: t.inputColor }}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label style={{ color: t.heading }}>Project Number</label>
                <input
                  type="text"
                  value={editedProject.project_number || ''}
                  onChange={(e) => handleEditChange('project_number', e.target.value)}
                  style={{ backgroundColor: t.inputBg, borderColor: t.inputBorder, color: t.inputColor }}
                />
              </div>
              <div className="form-group">
                <label style={{ color: t.heading }}>Client</label>
                <input
                  type="text"
                  value={editedProject.client_name || ''}
                  onChange={(e) => handleEditChange('client_name', e.target.value)}
                  style={{ backgroundColor: t.inputBg, borderColor: t.inputBorder, color: t.inputColor }}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label style={{ color: t.heading }}>Contract Value</label>
                <div style={{
                  padding: '10px 14px 10px 28px', fontSize: '14px', color: t.heading, fontWeight: 600,
                  backgroundColor: t.bgAlt, border: `1px solid ${t.cardBorder}`, borderRadius: '8px',
                  position: 'relative',
                }}>
                  <span style={{
                    position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                    color: t.textSecondary, fontSize: '14px',
                  }}>$</span>
                  {formatCurrencyInput(editedProject.revised_contract_value || editedProject.original_contract_value || editedProject.contract_amount || '0')}
                  <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 400, marginTop: '2px' }}>
                    Managed in Contract Value tab
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label style={{ color: t.heading }}>Project Manager(s)</label>
                <div style={{
                  border: `1px solid ${t.cardBorder}`, borderRadius: '8px', padding: '8px 12px',
                  backgroundColor: t.bg, minHeight: '40px',
                }}>
                  {/* Selected PMs as pills */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: (editedProject.project_manager ? 8 : 0) }}>
                    {(editedProject.project_manager || '').split(',').filter(Boolean).map((pm, i) => (
                      <span key={i} style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        padding: '4px 10px', borderRadius: '16px', fontSize: '12px', fontWeight: 600,
                        backgroundColor: t.dark ? '#1e3a5f' : '#eff6ff', color: '#3b82f6', border: `1px solid ${t.dark ? '#2563eb' : '#bfdbfe'}`,
                      }}>
                        {pm.trim()}
                        <span
                          onClick={() => {
                            const current = (editedProject.project_manager || '').split(',').filter(Boolean).map(s => s.trim());
                            const updated = current.filter((_, idx) => idx !== i);
                            handleEditChange('project_manager', updated.join(', '));
                          }}
                          style={{ cursor: 'pointer', fontSize: '14px', lineHeight: 1, color: '#93c5fd' }}
                        >&times;</span>
                      </span>
                    ))}
                  </div>
                  {/* Add PM dropdown */}
                  <select
                    value=""
                    onChange={(e) => {
                      if (!e.target.value) return;
                      const current = (editedProject.project_manager || '').split(',').filter(Boolean).map(s => s.trim());
                      if (!current.includes(e.target.value)) {
                        handleEditChange('project_manager', [...current, e.target.value].join(', '));
                      }
                    }}
                    style={{
                      width: '100%', padding: '6px 8px', fontSize: '13px', border: 'none',
                      backgroundColor: 'transparent', color: t.textSecondary, outline: 'none', cursor: 'pointer',
                    }}
                  >
                    <option value="">+ Add a project manager...</option>
                    {pmList
                      .filter(pm => !(editedProject.project_manager || '').split(',').map(s => s.trim()).includes(pm.name))
                      .map(pm => (
                        <option key={pm.id} value={pm.name}>{pm.name} ({pm.role_label})</option>
                      ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label style={{ color: t.heading }}>Start Date</label>
                <input
                  type="date"
                  value={editedProject.start_date || ''}
                  onChange={(e) => handleEditChange('start_date', e.target.value)}
                  style={{ backgroundColor: t.inputBg, borderColor: t.inputBorder, color: t.inputColor }}
                />
              </div>
              <div className="form-group">
                <label style={{ color: t.heading }}>End Date</label>
                <input
                  type="date"
                  value={editedProject.end_date || ''}
                  onChange={(e) => handleEditChange('end_date', e.target.value)}
                  style={{ backgroundColor: t.inputBg, borderColor: t.inputBorder, color: t.inputColor }}
                />
              </div>
            </div>

            <div className="form-group">
              <label style={{ color: t.heading }}>Description</label>
              <textarea
                value={editedProject.description || ''}
                onChange={(e) => handleEditChange('description', e.target.value)}
                rows="3"
                style={{ backgroundColor: t.inputBg, borderColor: t.inputBorder, color: t.inputColor }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
              <button
                className="btn btn-primary"
                onClick={handleSaveProject}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setEditedProject(project)}
              >
                Reset
              </button>
            </div>
          </div>
        )}

        {activeTab === 'sov' && (
          <SOVTab projectId={projectId} refreshSummary={refreshSummary} />
        )}
        {activeTab === 'tasks' && (
          <TasksTab projectId={projectId} refreshSummary={refreshSummary} />
        )}
        {activeTab === 'rollup' && (
          <RollupTab projectId={projectId} refreshSummary={refreshSummary} />
        )}
        {activeTab === 'gantt' && (
          <GanttTab projectId={projectId} refreshSummary={refreshSummary} onTaskOpen={(taskId) => setGanttEditTaskId(taskId)} refreshKey={ganttRefreshKey} />
        )}
        {activeTab === 'billing' && (
          <BillingTab projectId={projectId} refreshSummary={refreshSummary} />
        )}
        {activeTab === 'performance' && (
          <div>
            <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: t.bgAlt, borderRadius: 28, padding: 4, width: 'fit-content' }}>
              {[{ k: 'schedule', l: 'Schedule' }, { k: 'billing', l: 'Billing' }].map(tab => (
                <button key={tab.k} onClick={() => setPerfView(tab.k)} style={{
                  padding: '8px 24px', borderRadius: 24, border: 'none',
                  background: perfView === tab.k ? '#6366f1' : 'transparent',
                  color: perfView === tab.k ? '#fff' : t.textSecondary,
                  fontWeight: 600, fontSize: 13, cursor: 'pointer', transition: 'all .15s',
                  boxShadow: perfView === tab.k ? '0 2px 10px rgba(99,102,241,.3)' : 'none',
                }}>{tab.l}</button>
              ))}
            </div>
            {perfView === 'schedule' && <SchedulePerformanceTab projectId={projectId} />}
            {perfView === 'billing' && <BillingPerformanceSection projectId={projectId} />}
          </div>
        )}
        {activeTab === 'forecast' && (
          <ForecastTab projectId={projectId} refreshSummary={refreshSummary} />
        )}
        {activeTab === 'contract value' && (
          <ContractValueTab projectId={projectId} refreshSummary={refreshSummary} />
        )}
        {activeTab === 'job-costs' && (
          <JobCostTab projectId={projectId} project={project} />
        )}
        {activeTab === 'client view' && (
          <ClientViewTab projectId={projectId} refreshSummary={refreshSummary} />
        )}
      </div>

      {/* Task Edit Modal — opened from Gantt double-click */}
      {ganttEditTaskId && (
        <TaskEditModal
          taskId={ganttEditTaskId}
          projectId={projectId}
          onClose={() => setGanttEditTaskId(null)}
          onSaved={() => { refreshSummary(); setGanttRefreshKey(k => k + 1); }}
          onDeleted={() => { refreshSummary(); setGanttRefreshKey(k => k + 1); }}
        />
      )}
    </div>
  );
};

export default ProjectDetail;
